/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logLabelEntity } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { useEntities } from 'src/luis/projects/entities/hooks/useEntities';
import { ModelIdentifier } from 'src/luis/projects/utterances/handlers/utteranceHandlerGroup';
import { resolveLabel } from 'src/luis/projects/utterances/utils/utteranceUtils';
import { Model } from 'src/luis/types/Model';
import { ModelType } from 'src/luis/types/ModelType';
import { LuisId } from 'src/luis/types/Resource';
import { EntityLabelStore } from 'src/luis/types/stores/utterances/EntityLabelStore';

export type ConfirmPredictionActionSource = 'commandBar' | 'utteranceRow';

const messages = defineMessages({
    submittingUtterance: {
        id: 'portal.applicationsPage.submittingUtterance',
        defaultMessage: 'Submitting utterance ...'
    },
    submittingUtteranceCompleted: {
        id: 'portal.applicationsPage.submittingUtteranceCompleted',
        defaultMessage: 'Submit utterance completed'
    },
    deleteUtteranceToastStart: {
        id: 'portal.intentPage.deleteUtteranceToastStart',
        defaultMessage: 'Deleting utterance(s) ...'
    },
    deleteUtteranceToastSuccess: {
        id: 'portal.intentPage.deleteUtteranceToastSuccess',
        defaultMessage: 'Delete utterance(s) completed'
    },
    deleteUtteranceToastFailed: {
        id: 'portal.intentPage.deleteUtteranceToastFailed',
        defaultMessage: 'Delete utterance(s) failed'
    },
    confirmUtteranceToastRunning: {
        id: 'portal.intentPage.confirmUtteranceToastRunning',
        defaultMessage: 'Confirm predictions...'
    },
    confirmUtteranceToastSuccess: {
        id: 'portal.intentPage.confirmUtteranceToastSuccess',
        defaultMessage: 'Confirm predictions completed'
    }
});

/**
 * A hook for facilitating using utterance handlers.
 */
export const useUtteranceActions = ({ model }: ModelIdentifier) => {
    const { dispatcher } = useGlobalState();
    const { localize } = useLocalization();
    const { entities } = useEntities();

    const pageIndexTracker = useProgressTracker({ onComplete: () => dispatcher.dispatch('loadUtterancePage', { model }) });

    const eventLoggerRef = React.useRef(getEventLogger('IntentPage'));

    /**
     * Deletes the selected utterances.
     */
    const deleteUtterances = React.useCallback(
        (ids: LuisId[], tracker?: ProgressTrackerStore) => {
            dispatcher.dispatch('deleteUtterances', {
                model,
                ids,
                tracker,
                toastData: {
                    running: { title: localize(messages.deleteUtteranceToastStart) },
                    completed: { title: localize(messages.deleteUtteranceToastSuccess) },
                    failed: { title: localize(messages.deleteUtteranceToastFailed) }
                }
            });
        },
        [dispatcher.dispatch, model]
    );

    /**
     * Submits the current query as a new utterance.
     *
     * @param text The utterance text to add.
     */
    const submitUtterance = React.useCallback(
        (text: string) => {
            dispatcher.dispatch('addUtterances', {
                model,
                utterances: [{ text, labeledEntities: [], tokenizedText: [] }],
                newUtterances: true,
                toastData: {
                    running: { title: localize(messages.submittingUtterance) },
                    completed: { title: localize(messages.submittingUtteranceCompleted) }
                }
            });
        },
        [dispatcher.dispatch, model]
    );

    /**
     * Reassigns selected utterances to the given intent.
     *
     * @param destIntentId The intent id to reassign the
     * utterance to.
     */
    const reassignIntent = React.useCallback(
        (ids: LuisId[], destIntentId: string, tracker: ProgressTrackerStore) => {
            dispatcher.dispatch('reassignUtterances', {
                ids,
                srcIntentId: model.id,
                destIntentId,
                tracker
            });
        },
        [dispatcher.dispatch, model]
    );

    const confirmEntityPredictions = React.useCallback(
        (utteranceIds: LuisId[], method: ConfirmPredictionActionSource) => {
            switch (method) {
                case 'commandBar':
                    eventLoggerRef.current.log('ConfirmPredictionUsingTableCommandBar', { confirmedPredictionCount: utteranceIds.length });
                    break;

                case 'utteranceRow':
                    eventLoggerRef.current.log('ConfirmPredictionUsingUtteranceRowMenu', { confirmedPredictionCount: utteranceIds.length });
                    break;

                default:
                    break;
            }

            dispatcher.dispatch('confirmUtterancesPredictions', {
                intentId: model.typeId === ModelType.Intent ? model.id.toString() : undefined,
                utteranceIds,
                model,
                toastData: {
                    running: { title: localize(messages.confirmUtteranceToastRunning) },
                    completed: { title: localize(messages.confirmUtteranceToastSuccess) }
                }
            });
        },
        [dispatcher.dispatch, model]
    );

    const addLabel = React.useCallback(
        (
            id: LuisId,
            label: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>,
            method: 'brush' | 'menu' | 'createNewEntity'
        ) => {
            switch (method) {
                case 'brush':
                    logLabelEntity('Brush', 'luis');
                    break;

                case 'menu':
                    logLabelEntity('Menu', 'luis');
                    break;

                case 'createNewEntity':
                    logLabelEntity('Create', 'luis');
                    break;

                default:
                    break;
            }

            dispatcher.dispatch('submitLabels', {
                model,
                intentId: model.id.toString(),
                utteranceId: id,
                userAction: { name: 'AddLabel', label: resolveLabel(entities, label) }
            });
        },
        [dispatcher.dispatch, model]
    );

    const removeLabels = React.useCallback(
        (id: LuisId, labels: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>[]) =>
            dispatcher.dispatch('submitLabels', {
                model,
                intentId: model.id.toString(),
                utteranceId: id,
                userAction: { name: 'RemoveLabels', labels: labels.map(label => resolveLabel(entities, label)) }
            }),
        [dispatcher.dispatch, model]
    );

    /*
     * Submits the givin predictions as labels
     */
    const confirmPrediction = React.useCallback(
        (
            id: LuisId,
            labels: Pick<EntityLabelStore, 'entityId' | 'roleId' | 'startTokenIndex' | 'endTokenIndex'>[],
            method: 'predictionsMenu' | 'predictionsMenuIncludeSubEntities' | 'selectionMenu'
        ) => {
            switch (method) {
                case 'predictionsMenu':
                    eventLoggerRef.current.log('ConfirmPredictionUsingMenu');
                    break;

                case 'predictionsMenuIncludeSubEntities':
                    eventLoggerRef.current.log('ConfirmPredictionIncludeSubEntitiesUsingMenu');
                    break;

                case 'selectionMenu':
                    eventLoggerRef.current.log('ConfirmPredictionUsingSelectionMenu');
                    break;

                default:
                    break;
            }

            dispatcher.dispatch('submitLabels', {
                model,
                intentId: model.id.toString(),
                utteranceId: id,
                userAction: { name: 'ConfirmPredictions', labels: labels.map(label => resolveLabel(entities, label)) }
            });
        },
        [dispatcher.dispatch, model]
    );

    const addSynonymToSublist = React.useCallback(
        (entityId: LuisId, sublistName: string, value: string) => {
            dispatcher.dispatch('addSynonymToSublist', {
                id: entityId,
                name: sublistName,
                value
            });
        },
        [dispatcher.dispatch]
    );

    const createSublist = React.useCallback(
        (entityId: LuisId, sublistName: string, values: string[]) => {
            dispatcher.dispatch('addSublistToListEntity', {
                id: entityId,
                sublist: { name: sublistName, values }
            });
        },
        [dispatcher.dispatch]
    );

    const editUtterance = React.useCallback(
        (id: LuisId, newText: string, tracker: ProgressTrackerStore, intent?: Model) => {
            dispatcher.dispatch('editUtterance', {
                id,
                tracker,
                model,
                intent: model.typeId === ModelType.Intent ? model : intent,
                utterance: { text: newText, labeledEntities: [], tokenizedText: [] }
            });
        },
        [dispatcher.dispatch, model]
    );

    const changePageIndex = React.useCallback(
        (pageIndex: number) => {
            dispatcher.dispatch('changeUtterancePageIndex', { pageIndex, model, tracker: pageIndexTracker });
        },
        [dispatcher.dispatch, model]
    );

    return {
        submitUtterance,
        deleteUtterances,
        reassignIntent,
        confirmEntityPredictions,
        addLabel,
        removeLabels,
        confirmPrediction,
        addSynonymToSublist,
        createSublist,
        editUtterance,
        changePageIndex
    };
};
